import { Button, Caption1, Caption2, mergeClasses, Tooltip } from '@fluentui/react-components'
import {
  Dismiss12Regular,
  DocumentTableRegular,
  DocumentTextExtractRegular,
} from '@fluentui/react-icons'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HEADER_0, WORKING_DOCUMENT_ID } from '../../constants/files'
import { useFilePreviewDialog, useSelectedContext } from '../../hooks'
import { useTooltipControl } from '../../hooks/useTooltipControl'
import { formatTextSectionLabel } from '../../utils'
import { FileIcon } from '../FileIcon/FileIcon'
import { CONTEXT_ENTRY_ICON_SIZE } from './context-entry-styles'
import { useStyles } from './ContextCard.styles'

export interface ContextCardSection {
  id: string
  title: string
  type: 'heading' | 'text' | 'table'
}

interface ContextCardProps {
  id: string
}

const ContextCard = ({ id }: ContextCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { getFileSelection, updateFileSelection } = useSelectedContext()
  const { openFilePreviewDialog } = useFilePreviewDialog()
  const [isSectionInteractionActive, setIsSectionInteractionActive] = useState(false)
  const {
    visibleTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    tooltipContentClassName,
  } = useTooltipControl(2000)

  const file = getFileSelection(id)

  const isWorkingDocument = id === WORKING_DOCUMENT_ID
  const title = file?.fileName ?? ''
  const fileType = isWorkingDocument ? undefined : file?.extension

  const sections: ContextCardSection[] = useMemo(
    () =>
      (file?.sections ?? []).map(({ id, title, type }) => ({
        id,
        title,
        type,
      })),
    [file?.sections]
  )

  const handleRemoveFile = useCallback(() => {
    const selectedContextFile = getFileSelection(id)
    if (!selectedContextFile) return
    updateFileSelection({ ...selectedContextFile, sections: null })
  }, [id, getFileSelection, updateFileSelection])

  const handleRemoveSection = useCallback(
    (sectionId: string) => {
      const selectedContextFile = getFileSelection(id)
      if (!selectedContextFile || selectedContextFile.sections === null) return

      const nextSections = selectedContextFile.sections.filter(
        (section) => section.id !== sectionId
      )

      updateFileSelection({
        ...selectedContextFile,
        sections: nextSections.length === 0 ? null : nextSections,
      })
    },
    [id, getFileSelection, updateFileSelection]
  )

  const hasSelectedSections = sections.length > 0
  const isWholeFileSelected = file !== undefined && !hasSelectedSections

  const handlePreview = useCallback(() => {
    if (!file) return
    openFilePreviewDialog({ fileId: file.id, fileName: file.fileName })
  }, [openFilePreviewDialog, file])

  const handleSectionPreview = useCallback(
    (sectionId: string, sectionTitle: string, sectionType: ContextCardSection['type']) => {
      if (!file) return
      openFilePreviewDialog({
        fileId: file.id,
        fileName: file.fileName,
        sectionId,
        sectionTitle,
        sectionType,
      })
    },
    [openFilePreviewDialog, file]
  )

  return (
    <div
      className={mergeClasses(
        classes.card,
        isSectionInteractionActive && classes.cardSectionInteraction,
        isWholeFileSelected && classes.cardWholeFileSelected
      )}
      role="listitem"
      aria-label={title}
      data-testid={`context-card-${id}`}>
      <div className={classes.titleRow}>
        <div className={classes.titleLeft}>
          {fileType !== undefined && (
            <FileIcon
              fileType={fileType}
              width={CONTEXT_ENTRY_ICON_SIZE}
              height={CONTEXT_ENTRY_ICON_SIZE}
            />
          )}
          <Tooltip
            content={{ children: title, className: tooltipContentClassName }}
            relationship="label"
            appearance="inverted"
            visible={visibleTooltip === id}>
            <div
              className={classes.titleTextContainer}
              onMouseEnter={() => handleMouseEnter(id)}
              onMouseLeave={() => handleMouseLeave(id)}
              onFocus={() => handleFocus(id)}
              onBlur={() => handleBlur(id)}>
              <Caption1 as="span" className={mergeClasses(classes.titleText, 'title-text')}>
                {isWorkingDocument ? (
                  title
                ) : (
                  <Button
                    size="small"
                    appearance="transparent"
                    className={classes.titleLinkButton}
                    aria-label={t('files.actions.previewFile', { fileName: title })}
                    onClick={handlePreview}>
                    <span className={classes.buttonTextContainer}>{title}</span>
                  </Button>
                )}
              </Caption1>
              {isWorkingDocument && (
                <Caption2 as="span" className={classes.titleSubtitle}>
                  {t('files.workingDocumentFileName')}
                </Caption2>
              )}
            </div>
          </Tooltip>
        </div>
        <Button
          size="small"
          shape="circular"
          appearance="transparent"
          icon={<Dismiss12Regular />}
          aria-label={t('contextTile.removeFile', { fileName: title })}
          className={mergeClasses(classes.dismissButton, 'context-dismiss-btn')}
          onClick={handleRemoveFile}
        />
      </div>

      {sections.length > 0 && (
        <div
          role="list"
          className={mergeClasses(
            classes.sectionsGrid,
            sections.length === 1 && classes.sectionsGridSingle
          )}>
          {sections.map((section) => {
            const displayTitle =
              section.type === 'text'
                ? formatTextSectionLabel(section.title, t('files.sectionTextContentLabel'))
                : section.title === HEADER_0
                  ? t('files.headerZeroLabel')
                  : section.title

            return (
              <Tooltip
                key={section.id}
                content={{ children: displayTitle, className: tooltipContentClassName }}
                relationship="label"
                appearance="inverted"
                visible={visibleTooltip === section.id}>
                <div
                  className={classes.sectionTag}
                  role="listitem"
                  onMouseEnter={() => {
                    setIsSectionInteractionActive(true)
                    handleMouseEnter(section.id)
                  }}
                  onMouseLeave={() => {
                    setIsSectionInteractionActive(false)
                    handleMouseLeave(section.id)
                  }}
                  onFocusCapture={() => {
                    setIsSectionInteractionActive(true)
                    handleFocus(section.id)
                  }}
                  onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setIsSectionInteractionActive(false)
                      handleBlur(section.id)
                    }
                  }}>
                  {
                    section.type === 'table' ? (
                      <DocumentTableRegular
                        className={mergeClasses(classes.sectionIcon, 'context-section-icon')}
                        fontSize={20}
                      />
                    ) : section.type === 'text' ? (
                      <DocumentTextExtractRegular
                        className={mergeClasses(classes.sectionIcon, 'context-section-icon')}
                        fontSize={20}
                      />
                    ) : null // For headings, we currently show no icon and just use the formatted title
                  }
                  <Caption1
                    as="span"
                    className={mergeClasses(classes.sectionTagText, 'section-tag-text')}>
                    {isWorkingDocument ? (
                      displayTitle
                    ) : (
                      <Button
                        size="small"
                        appearance="transparent"
                        className={classes.sectionLinkButton}
                        aria-label={
                          section.type === 'table'
                            ? t('files.actions.previewTable', { sectionTitle: displayTitle })
                            : t('files.actions.previewSection', { sectionTitle: displayTitle })
                        }
                        onClick={() =>
                          handleSectionPreview(section.id, displayTitle, section.type)
                        }>
                        <span className={classes.buttonTextContainer}>{displayTitle}</span>
                      </Button>
                    )}
                  </Caption1>
                  <Button
                    size="small"
                    shape="circular"
                    appearance="subtle"
                    icon={<Dismiss12Regular />}
                    aria-label={
                      section.type === 'table'
                        ? t('contextTile.removeTable', { sectionTitle: displayTitle })
                        : t('contextTile.removeSection', { sectionTitle: displayTitle })
                    }
                    className={mergeClasses(classes.sectionDismissButton, 'section-dismiss-btn')}
                    onClick={() => {
                      handleRemoveSection(section.id)
                    }}
                  />
                </div>
              </Tooltip>
            )
          })}
        </div>
      )}
    </div>
  )
}

export { ContextCard }
